// =============================================================================
// src/lib/api-base.ts
// Pick the developer API host based on which domain the SPA is being
// served from.
//
//   geoborder.us             -> api.geoborder.us
//   geoborder.metaphase.tech -> api.geoborder.metaphase.tech
//   anything else (Netlify
//   deploy preview, localhost,
//   tests with no window)    -> api.geoborder.us  (canonical default)
//
// The metaphase.tech alias exists so CBP / similar networks that block
// the geoborder.us TLD can still reach the API. The two hosts are
// independent Netlify rewrites onto the same Supabase Edge Functions —
// responses are byte-identical regardless of host.
//
// Host matching is an exact-set lookup, NOT endsWith('metaphase.tech') —
// the loose form would also match an attacker-controlled
// 'evilmetaphase.tech', same pitfall the origin.ts allowlist comment
// already warns about. Browsers can't actually serve our SPA from such
// a host, but tight matching is cheap defense in depth.
//
// Both functions accept an optional `host` argument so unit tests can
// exercise the routing without a window mock. In normal SPA use the
// argument is omitted and the value falls back to window.location.host.
// =============================================================================

const CANONICAL_HOST = 'api.geoborder.us'
const METAPHASE_HOST = 'api.geoborder.metaphase.tech'

/** Hosts that should route to the metaphase.tech API surface. */
const METAPHASE_SPA_HOSTS: ReadonlySet<string> = new Set([
  'geoborder.metaphase.tech',
])

function currentHost(): string {
  return typeof window !== 'undefined' ? window.location.host : ''
}

/** Bare hostname for the developer API (no scheme, no path). */
export function getApiHost(host: string = currentHost()): string {
  return METAPHASE_SPA_HOSTS.has(host) ? METAPHASE_HOST : CANONICAL_HOST
}

/** Full base URL for /v1/* endpoints, no trailing slash. */
export function getApiBase(host: string = currentHost()): string {
  return `https://${getApiHost(host)}/v1`
}
